import React, { useState } from 'react';
import { Upload, Shield, Zap, Brain, FileCheck, FileText, Wifi, CreditCard, RefreshCw, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import UploadZone from '../components/UploadZone';
import { contractTypes, howItWorksSteps, pricingPlans, faqs, trustPoints } from '../data/mock';
import { trackCTAClick, trackSectionView, trackFAQExpand, trackExampleReportView, trackUploadStart } from '../utils/analytics';

const iconMap = {
  Upload, Shield, Zap, Brain, FileCheck, FileText, Wifi, CreditCard, RefreshCw, AlertCircle, CheckCircle
};

const Home = () => {
  const [showUpload, setShowUpload] = useState(false);

  const handleUploadComplete = (data) => {
    console.log('Upload completed:', data);
    // Redirect handled by UploadZone component
  };

  const scrollToUpload = () => {
    trackCTAClick('Carica contratto', 'Navigation');
    trackUploadStart();
    setShowUpload(true);
    setTimeout(() => {
      document.getElementById('upload-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-28 md:h-32">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clauscheck/artifacts/llmpg64m_ChatGPT%20Image%208%20mar%202026%2C%2018_40_06.png" 
                alt="PRIMA LEGGO - Analisi contratto online, verifica clausole nascoste prima di firmare" 
                title="PRIMA LEGGO - Controlla contratto prima di firmare"
                className="h-24 md:h-32 w-auto cursor-pointer hover:scale-105 transition-transform drop-shadow-lg"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                loading="eager"
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#come-funziona" className="text-gray-700 hover:text-green-600 transition-colors font-medium">Come funziona</a>
              <a href="#prezzi" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Prezzi</a>
              <a href="#esempio-analisi" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Esempio</a>
              <a href="#faq" className="text-gray-700 hover:text-green-600 transition-colors font-medium">FAQ</a>
            </nav>
            <Button onClick={scrollToUpload} className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white font-semibold shadow-lg">
              Carica contratto
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
              ✓ Proteggi i tuoi interessi
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 mb-6 leading-tight">
            Prima di firmare un contratto,<br />
            <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
              assicurati di averlo capito
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto font-medium">
            Analizza contratto online: scopri clausole nascoste, clausole vessatorie, costi inaspettati e rischi prima di firmare
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={scrollToUpload}
              size="lg" 
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-xl px-10 py-7 h-auto font-bold shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              <Upload className="w-6 h-6 mr-2" />
              Carica il tuo contratto
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => {
                trackCTAClick('Come funziona', 'Hero');
                document.getElementById('come-funziona')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-xl px-10 py-7 h-auto font-semibold"
            >
              Come funziona
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="come-funziona" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">Come funziona</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Tre semplici passaggi per capire cosa stai firmando e verificare le clausole del contratto
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {howItWorksSteps.map((step, index) => {
              const Icon = iconMap[step.icon];
              const colors = ['bg-blue-500', 'bg-green-500', 'bg-blue-600'];
              const bgColors = ['bg-blue-100', 'bg-green-100', 'bg-blue-100'];
              return (
                <div key={step.step} className="relative text-center group hover:scale-105 transition-transform">
                  <div className={`inline-flex items-center justify-center w-24 h-24 rounded-2xl ${colors[index]} mb-6 shadow-xl group-hover:shadow-2xl transition-shadow`}>
                    <Icon className="w-12 h-12 text-white" />
                  </div>
                  <div className="absolute top-10 left-1/2 -translate-x-1/2 -z-10">
                    <span className={`text-9xl font-black ${bgColors[index].replace('bg-', 'text-')} opacity-20`}>{step.step}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-lg text-gray-600">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contract Types */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 via-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">Tipi di contratti supportati</h2>
            <p className="text-xl text-gray-600">Analizziamo ogni tipo di contratto consumer</p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {contractTypes.map((contract, index) => {
              const Icon = iconMap[contract.icon];
              const colors = ['bg-blue-600', 'bg-green-600', 'bg-blue-500', 'bg-green-500', 'bg-blue-600'];
              const bgColors = ['bg-blue-50', 'bg-green-50', 'bg-blue-50', 'bg-green-50', 'bg-blue-50'];
              return (
                <Card key={contract.id} className={`p-6 text-center hover:shadow-2xl transition-all hover:scale-105 ${bgColors[index]} border-2 border-transparent hover:border-${colors[index].replace('bg-', '')}`}>
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl ${colors[index]} mb-4 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 text-lg">{contract.title}</h3>
                  <p className="text-sm text-gray-600">{contract.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="prezzi" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-8 py-3 rounded-full text-lg font-bold shadow-xl animate-pulse">
                🎉 OFFERTA LANCIO: -60% per i primi clienti!
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">Scegli il report più adatto</h2>
            <p className="text-xl text-gray-600">Analisi chiara e professionale a prezzi incredibili</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`p-8 relative hover:shadow-2xl transition-all hover:scale-105 ${
                  plan.popular ? 'border-4 border-green-500 shadow-2xl bg-gradient-to-br from-green-50 to-blue-50' : 'border-2 border-blue-300 bg-blue-50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-full text-base font-bold shadow-xl">
                      ⭐ Più scelto
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-3xl font-extrabold text-gray-900 mb-4">{plan.name}</h3>
                  {plan.promo && (
                    <div className="mb-2">
                      <span className="text-2xl text-gray-400 line-through mr-3">{plan.originalPrice}{plan.currency}</span>
                      <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        PROMO -60%
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-center mb-4">
                    <span className="text-7xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">{plan.price}</span>
                    <span className="text-3xl text-gray-600 ml-2 font-bold">{plan.currency}</span>
                  </div>
                  {plan.promo && (
                    <p className="text-sm text-gray-500 font-medium">
                      Prezzo normale: {plan.originalPrice}{plan.currency}
                    </p>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className={`w-6 h-6 ${plan.popular ? 'text-green-600' : 'text-blue-600'} mr-3 mt-0.5 flex-shrink-0`} />
                      <span className="text-gray-800 font-medium text-lg">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => {
                    trackCTAClick(`Inizia analisi ${plan.name}`, 'Pricing');
                    scrollToUpload();
                  }}
                  className={`w-full text-lg py-6 font-bold shadow-xl transition-all ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white hover:scale-105' 
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white hover:scale-105'
                  }`}
                >
                  Inizia analisi
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <button
                  onClick={() => {
                    trackExampleReportView(plan.id);
                    window.open(`/report/${plan.id}`, '_blank');
                  }}
                  className="w-full mt-3 text-center text-sm text-gray-600 hover:text-blue-600 font-medium underline"
                >
                  👁️ Vedi esempio report {plan.name}
                </button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Esempio di Analisi */}
      <section id="esempio-analisi" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="example-report-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-blue-100 text-blue-700 px-6 py-2 rounded-full text-base font-bold mb-6">
              📄 Analisi reale (anonimizzata)
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">Guarda un esempio di report</h2>
            <p className="text-xl text-gray-600">Ecco cosa abbiamo trovato in una vera bolletta della luce</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-blue-200">
            {/* Mini verdetto */}
            <div className="bg-yellow-50 border-b-4 border-yellow-400 p-6 md:p-8">
              <div className="flex items-start space-x-4">
                <AlertCircle className="w-10 h-10 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl md:text-3xl font-black text-yellow-700 mb-2">Verdetto: Migliorabile</h3>
                  <p className="text-gray-700 text-lg">Il costo per kWh è superiore alla media ARERA e ci sono costi accessori poco trasparenti.</p>
                </div>
              </div>
            </div>

            {/* Mini rischi */}
            <div className="p-6 md:p-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">⚠️ Cosa ha trovato l'analisi:</h4>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                  <span className="text-red-600 font-bold">1.</span>
                  <p className="text-gray-800">Costo per kWh di <strong>€0,183</strong> contro una media ARERA di €0,10-0,13</p>
                </li>
                <li className="flex items-start space-x-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                  <span className="text-red-600 font-bold">2.</span>
                  <p className="text-gray-800">Interessi di mora elevati (BCE + 3,5%) in caso di ritardo nel pagamento</p>
                </li>
                <li className="flex items-start space-x-3 p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                  <span className="text-yellow-600 font-bold">💰</span>
                  <p className="text-gray-800">Costi nascosti: €10,52 di trasporto e gestione contatore non dettagliati</p>
                </li>
              </ul>

              <div className="mt-8 text-center">
                <Button
                  data-testid="view-example-report-button"
                  onClick={() => {
                    trackExampleReportView('premium');
                    window.open('/report/premium', '_blank');
                  }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white text-lg py-6 px-10 font-bold shadow-xl hover:scale-105 transition-all"
                >
                  Vedi il report completo di esempio
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-sm text-gray-500 mt-4">Gratis, senza registrazione: scopri esattamente cosa riceverai</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-500 via-blue-600 to-green-500 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">Perché fidarsi di PRIMA LEGGO</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {trustPoints.map((point, index) => {
              const Icon = iconMap[point.icon];
              return (
                <div key={index} className="text-center bg-white/10 backdrop-blur-lg rounded-2xl p-8 hover:bg-white/20 transition-all hover:scale-105">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white mb-6 shadow-xl">
                    <Icon className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{point.title}</h3>
                  <p className="text-blue-50 text-lg">{point.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 p-6 bg-white rounded-2xl border-4 border-green-400 max-w-3xl mx-auto shadow-2xl">
            <p className="text-center text-gray-800 font-medium text-lg">
              <AlertCircle className="w-6 h-6 inline mr-2 text-green-600" />
              Questo è un servizio di analisi informativa e non sostituisce la consulenza legale professionale.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">Domande frequenti</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => {
              const borderColors = ['border-blue-300', 'border-green-300', 'border-blue-300'];
              const hoverColors = ['hover:border-blue-500', 'hover:border-green-500', 'hover:border-blue-500'];
              return (
                <AccordionItem 
                  key={faq.id} 
                  value={`item-${faq.id}`}
                  className={`border-2 ${borderColors[index]} ${hoverColors[index]} rounded-xl px-6 bg-white shadow-md hover:shadow-xl transition-all`}
                >
                  <AccordionTrigger 
                    className="text-left font-bold text-gray-900 hover:text-green-600 text-lg"
                    onClick={() => trackFAQExpand(faq.question)}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pt-2 text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Upload Section */}
      <section id="upload-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-100 via-white to-green-100">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              Non firmare alla cieca
            </span>
          </h2>
          <p className="text-2xl text-gray-600 mb-12 font-medium">
            Carica il tuo contratto ora e scopri cosa contiene veramente
          </p>
          
          {showUpload && <UploadZone onUploadComplete={handleUploadComplete} />}
          
          {!showUpload && (
            <Button 
              onClick={() => {
                trackCTAClick('Carica contratto ora', 'Final CTA');
                trackUploadStart();
                setShowUpload(true);
              }}
              size="lg"
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-xl px-12 py-8 h-auto font-bold shadow-2xl hover:scale-105 transition-all"
            >
              <Upload className="w-6 h-6 mr-3" />
              Carica il tuo contratto ora
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 via-blue-900 to-green-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4 bg-white p-3 rounded-xl inline-block shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_clauscheck/artifacts/llmpg64m_ChatGPT%20Image%208%20mar%202026%2C%2018_40_06.png" 
                  alt="PRIMA LEGGO Logo - Servizio analisi contratto online" 
                  title="PRIMA LEGGO - Verifica contratto online"
                  className="h-14 w-auto"
                  loading="lazy"
                />
              </div>
              <p className="text-gray-300 text-lg">
                Analisi intelligente dei contratti per consumatori consapevoli
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-xl text-green-400">Servizio</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#come-funziona" className="hover:text-green-400 transition-colors">Come funziona</a></li>
                <li><a href="#prezzi" className="hover:text-green-400 transition-colors">Prezzi</a></li>
                <li><a href="#faq" className="hover:text-green-400 transition-colors">FAQ</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-xl text-blue-400">Legale</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-blue-400 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Termini di Servizio</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 text-center text-gray-300">
            <p>&copy; 2025 PRIMA LEGGO. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
